import type { LiveGateFoupState, LiveOhtRuntimeSnapshot } from '../../types/amhsStream';
import { EQUIPMENT_TRANSFER_FRACS } from '../../components/canvas/amhs/Equipment/equipmentPortTransfer';
import {
  STOCKER_DROP_LP_RELEASE_PROGRESS,
  STOCKER_PICKUP_OHT_TAKE_PROGRESS,
} from '../../components/canvas/amhs/Stocker/stockerTransferTiming';

/** Track-level lifter handoff (FabAmhsLifter3D / OhtVehicle3DModel). */
export const OHT_TRACK_DROP_RELEASE_PROGRESS = 0.15;
export const OHT_TRACK_PICKUP_TAKE_PROGRESS = EQUIPMENT_TRANSFER_FRACS.pickupOhtTake;

export function isStockerHandoffPort(portId: string, stationId?: string | null): boolean {
  if (stationId?.trim() && /^(?:ST|STOCKER)[-_]/i.test(stationId.trim())) return true;
  return /^(?:ST|STOCKER)[-_]/i.test(portId.trim());
}

export type OhtCarriedFoupVisibleInput = {
  carriedFoupId?: string | null;
  transfer?: {
    action: 'PICKUP' | 'DROP';
    progress01: number;
    portId?: string;
    stationId?: string;
  } | null;
  gateFoupState?: LiveGateFoupState | null;
};

/**
 * Single rule for whether the OHT should draw a carried FOUP.
 * Ledger ownership can lag or lead the lifter animation; during an active
 * transfer, progress beats `carriedFoupId`.
 */
export function resolveOhtCarriedFoupVisible(input: OhtCarriedFoupVisibleInput): boolean {
  const gateState = input.gateFoupState;
  if (gateState != null) {
    if (gateState === 'ON_OHT') return true;
    if (
      gateState === 'IN_ENTRY_EQUIPMENT' ||
      gateState === 'IN_EXIT_EQUIPMENT' ||
      gateState === 'IN_TRANSFER'
    ) {
      return false;
    }
  }

  const transfer = input.transfer;
  if (transfer) {
    const p = Math.min(1, Math.max(0, transfer.progress01));
    const stocker = isStockerHandoffPort(transfer.portId ?? '', transfer.stationId);
    if (transfer.action === 'PICKUP') {
      // Once the scheduler/ledger has committed carry, never hide again for this
      // pickup span — progress01 and hoist animation can briefly disagree.
      if (input.carriedFoupId != null) return true;
      const take = stocker ? STOCKER_PICKUP_OHT_TAKE_PROGRESS : OHT_TRACK_PICKUP_TAKE_PROGRESS;
      return p + 1e-6 >= take;
    }
    if (transfer.action === 'DROP') {
      const release = stocker ? STOCKER_DROP_LP_RELEASE_PROGRESS : OHT_TRACK_DROP_RELEASE_PROGRESS;
      return p + 1e-6 < release;
    }
  }

  return input.carriedFoupId != null;
}

export function resolveOhtCarriedFoupVisibleFromRuntime(
  runtime: LiveOhtRuntimeSnapshot | null | undefined
): boolean {
  if (!runtime) return false;
  return resolveOhtCarriedFoupVisible({
    carriedFoupId: runtime.carriedFoupId,
    gateFoupState: runtime.gateTransfer?.foupState,
    transfer: runtime.transfer
      ? {
          action: runtime.transfer.action,
          progress01: runtime.transfer.progress01,
          portId: runtime.transfer.portId,
          stationId: runtime.transfer.stationId,
        }
      : null,
  });
}
